/**
 * Authentication context for the frontend application.
 *
 * Provides:
 * - Login/logout functionality
 * - Current user state (profile, loading, error)
 * - Automatic token persistence in localStorage
 * - Token refresh on app load (validates stored token via /auth/me)
 * - Role-based access helpers
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LoginRequest, LoginResponse, UserProfile } from 'shared';
import { UserRole } from 'shared';
import { apiRequest, setAccessToken, setRefreshHandler } from '../services/api';

// ─── Storage Keys ───────────────────────────────────────────────────────────

const TOKEN_KEY = 'smp_access_token';
const REFRESH_TOKEN_KEY = 'smp_refresh_token';

// ─── Context Types ──────────────────────────────────────────────────────────

export interface AuthState {
  /** Current authenticated user, or null if not logged in */
  user: UserProfile | null;
  /** Whether the initial auth check is in progress */
  isLoading: boolean;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Last auth error message */
  error: string | null;
}

export interface AuthActions {
  /** Log in with email and password */
  login: (credentials: LoginRequest) => Promise<boolean>;
  /** Log out and clear tokens */
  logout: () => void;
  /** Clear the current error */
  clearError: () => void;
}

export type AuthContextValue = AuthState & AuthActions;

// ─── Context ────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = user !== null;

  /**
   * Persist tokens to localStorage.
   */
  const persistTokens = useCallback((accessToken: string, refreshToken?: string) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    setAccessToken(accessToken);
  }, []);

  /**
   * Clear tokens from localStorage and memory.
   */
  const clearTokens = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setAccessToken(null);
  }, []);

  /**
   * Fetch the current user profile using the stored token.
   * Returns the user profile or null if the token is invalid.
   */
  const fetchCurrentUser = useCallback(async (): Promise<UserProfile | null> => {
    const result = await apiRequest<UserProfile>('/auth/me');
    if (result.success) {
      return result.data;
    }
    return null;
  }, []);

  /**
   * Attempt to refresh the access token using the stored refresh token.
   * Returns the new access token or null on failure.
   */
  const refreshToken = useCallback(async (): Promise<string | null> => {
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!storedRefresh) return null;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'}/auth/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: storedRefresh }),
        },
      );

      if (!response.ok) {
        clearTokens();
        return null;
      }

      const json = await response.json();
      if (json.success && json.data?.token) {
        const newToken = json.data.token as string;
        localStorage.setItem(TOKEN_KEY, newToken);
        if (json.data.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, json.data.refreshToken as string);
        }
        setAccessToken(newToken);
        return newToken;
      }

      clearTokens();
      return null;
    } catch {
      clearTokens();
      return null;
    }
  }, [clearTokens]);

  /**
   * Log in with email and password.
   * Returns true on success, false on failure (sets error state).
   */
  const login = useCallback(
    async (credentials: LoginRequest): Promise<boolean> => {
      setError(null);

      const result = await apiRequest<LoginResponse>('/auth/login', {
        method: 'POST',
        body: credentials,
        skipAuth: true,
      });

      if (!result.success) {
        setError(result.error.message);
        return false;
      }

      const { token, user: userProfile } = result.data;
      persistTokens(token);
      setUser(userProfile);
      return true;
    },
    [persistTokens],
  );

  /**
   * Log out: clear tokens and user state.
   */
  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
    setError(null);
  }, [clearTokens]);

  /**
   * Clear the current error.
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * On mount, check for a stored token and validate it.
   */
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      // Set the token so apiRequest can use it
      setAccessToken(storedToken);

      const currentUser = await fetchCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      } else {
        // Token might be expired, try refresh
        const newToken = await refreshToken();
        if (newToken) {
          const refreshedUser = await fetchCurrentUser();
          if (refreshedUser) {
            setUser(refreshedUser);
          } else {
            clearTokens();
          }
        } else {
          clearTokens();
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, [fetchCurrentUser, refreshToken, clearTokens]);

  /**
   * Register the refresh handler with the API client so it can auto-refresh on 401.
   */
  useEffect(() => {
    setRefreshHandler(refreshToken);
    return () => setRefreshHandler(null);
  }, [refreshToken]);

  /**
   * Proactive inactivity timeout (8 hours).
   * Resets on any user interaction (mouse, keyboard, touch, scroll).
   * Logs out automatically when idle for too long.
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const INACTIVITY_MS = 8 * 60 * 60 * 1000; // 8 hours
    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        logout();
        // Optionally show a message — the login page will render next
      }, INACTIVITY_MS);
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start on mount

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [isAuthenticated, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated,
      error,
      login,
      logout,
      clearError,
    }),
    [user, isLoading, isAuthenticated, error, login, logout, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Access the auth context. Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ─── Role Helpers ───────────────────────────────────────────────────────────

/**
 * Check if a user has admin role.
 */
export function isAdmin(user: UserProfile | null): boolean {
  return user?.role === UserRole.Admin;
}

/**
 * Check if a user has any company role (admin, user, or viewer).
 */
export function isCompanyMember(user: UserProfile | null): boolean {
  if (!user) return false;
  return (
    user.role === UserRole.CompanyAdmin ||
    user.role === UserRole.CompanyUser ||
    user.role === UserRole.CompanyViewer
  );
}
